import { useMemo, useState } from "react";
import { Box } from "@/ui";
import { MotionSensKeysArr, MotionStringKeys } from "@/enums/motion";
import { getHasActiveSubscription } from "@/store/auth/selectors";
import { useAuth } from "@/store/auth";
import { isStationOffline } from "@/utils/isStationOffline";
import { t } from "@/utils";
import { usePremiumScreen } from "@/modules/app/PremiumScreen";
import { usePresenceKeeper } from "@/modules/app/Presence";
import ListButton from "@/components/ListButton";
import ListField from "@/components/ListField";
import { OptionsMenuProps } from "@/components/Menu";
import NavigationBar from "@/components/NavigationBar";
import CameraOfflineDialog from "../components/CameraOfflineDialog";

import ConfigureIcon from "assets/icons/configure.svg?react";
import DetectionZonesIcon from "assets/icons/detection_zone.svg?react";

export default function MotionDetection({
  push,
  cameraSettings,
  updateSettings,
  hasDetectionZonesSupport,
  xmppLogin,
  Progress
}: CSTypes.CameraSettingsStepProps) {
  const [isCameraOfflineDialogOpen, setIsCameraOfflineDialogOpen] = useState(false);

  const { motionSensitivity, motionDetectionEnabled } = cameraSettings.settings;
  const cameraPresence = usePresenceKeeper()[xmppLogin];
  const isCameraOffline = isStationOffline(cameraPresence);
  const { displayBannerForFreeUser } = usePremiumScreen();

  const motionSensitivityItems: OptionsMenuProps["items"] = useMemo(() => {
    return MotionSensKeysArr.map((value) => ({
      item: t(MotionStringKeys[value]),
      value
    }));
  }, []);

  const isPremium = useAuth(getHasActiveSubscription);

  return (
    <Box>
      {Progress && <Progress />}
      <NavigationBar
        title={t("motionDetection.heading")}
        onBack={() => push("settings", { stepDirection: "previous" })}
        props={{ mb: "8" }}
      />
      <ListField
        iconFill="primary"
        props={{
          mb: "48"
        }}
        label={t("motionDetection.heading")}
        switch={{
          isChecked: motionDetectionEnabled,
          onChange: () => {
            updateSettings({
              settings: {
                motionDetectionEnabled: !motionDetectionEnabled
              }
            });
          }
        }}
      />
      <ListField
        icon={ConfigureIcon}
        disabled={!motionDetectionEnabled}
        props={{
          mb: "12"
        }}
        iconFill="primary"
        label={t("sensitivity")}
        additionalText={t("motionDetection.description")}
        menuOptions={{
          items: motionSensitivityItems,
          label: t(MotionStringKeys[motionSensitivity]),
          menuOptionGroupProps: {
            value: motionSensitivity,
            onChange: (sens) =>
              updateSettings({
                settings: {
                  motionSensitivity: sens as MotionDetectionSensitivity
                }
              })
          }
        }}
      />
      {hasDetectionZonesSupport && (
        <ListButton
          isDisabled={!motionDetectionEnabled}
          icon={DetectionZonesIcon}
          iconFill="primary"
          label={t("detectionZone")}
          additionalText={isPremium && cameraSettings.settings.motionDetectionZone ? t("custom") : t("everywhere")}
          props={{
            width: "100%"
          }}
          onClick={() => {
            if (isCameraOffline) setIsCameraOfflineDialogOpen(true);
            else displayBannerForFreeUser("detection_zones").elseRun(() => push("detection-zones"));
          }}
        />
      )}
      <CameraOfflineDialog isOpen={isCameraOfflineDialogOpen} onClose={() => setIsCameraOfflineDialogOpen(false)} />
    </Box>
  );
}
